import { type TextEngine, type WritingModeValue } from '@meitu/x-design-text';
import { isFiniteNumber } from '@/utils/math-kit';
import {
    measureBuffer,
    measureListMarker,
    type MeasureBufferInput,
    type MeasureListMarkerInput,
} from '@/utils/text-kit';
import { TextWritingType, type ILayerProps, type ISize, type ITextLayerProps } from '@/version-types';
import { DEFAULT_LINE_HEIGHT_FACTOR } from '../constants';
import { resolveMainFontFamily } from './font';
import { countListParagraphs, isCharListStyle } from './list';
import { createRichTextSpans } from './span';

/** CJK / 全角字符：按 1em 估算；其余按 0.5em */
const WIDE_CHAR_RE = /[\u1100-\u11FF\u2E80-\uA4CF\uAC00-\uD7AF\uF900-\uFAFF\uFE30-\uFE6F\uFF00-\uFF60\uFFE0-\uFFE6]/;

const estimateCharAdvance = (char: string, fontSize: number) => (WIDE_CHAR_RE.test(char) ? fontSize : fontSize * 0.5);

const estimateLineAdvance = (line: string, fontSize: number, letterSpacing: number) => {
    if (!line) return fontSize;

    let advance = 0;
    for (const char of line) {
        advance += estimateCharAdvance(char, fontSize);
    }

    if (line.length > 1) {
        advance += letterSpacing * (line.length - 1);
    }

    return advance;
};

/**
 * 在文本引擎不可用时，按字符数 / 字号 / 行高粗略估算图层尺寸。
 * 横排：宽≈最长行字宽，高≈行数×字号×行高；竖排时对调。
 */
export const estimateLayerSize = (layer: ILayerProps): ISize => {
    const data = layer.data as ITextLayerProps;
    const fontSize = Math.max(data.fontSize || 0);
    const letterSpacing = isFiniteNumber(data.letterSpacing) ? data.letterSpacing : 0;
    const lineSpacing = data.lineSpacing || DEFAULT_LINE_HEIGHT_FACTOR;
    const lines = (data.text || '').split('\n');
    const lineCount = Math.max(lines.length, 1);

    const maxLineAdvance = Math.max(
        ...lines.map((line) => estimateLineAdvance(line, fontSize, letterSpacing)),
        fontSize,
    );
    const crossAxisSize = lineCount * fontSize * lineSpacing;

    const isVertical = data.writingMode !== TextWritingType.HORIZONTAL_TB;

    return isVertical
        ? { width: crossAxisSize, height: maxLineAdvance }
        : { width: maxLineAdvance, height: crossAxisSize };
};

/**
 * 创建文本引擎 buffer 测量入参。
 *
 * 本函数只读取图层数据并派生测量所需字段，不触发字体加载、资源加载、测量或图层宽高写回。
 * 垂直排版和一阶段暂不支持的高级能力不会进入测量入参，后续渲染能力按实际 builder 接入。
 *
 * @param layer - ILayerProps，当前需要渲染的文本图层。
 * @returns MeasureBufferInput，U1 buffer 测量入口可消费的入参。
 */
export const createMeasureInput = (layer: ILayerProps): MeasureBufferInput => {
    const data = layer.data as ITextLayerProps;
    const fixedInlineSize = layer.__fixedWidth !== false;
    const writingMode = data.writingMode as WritingModeValue;
    const isHorizontal = writingMode === 'horizontal-tb';

    return {
        text: data.text,
        fontSize: data.fontSize,
        lineHeightScale: data.lineSpacing,
        letterSpacing: isFiniteNumber(data.letterSpacing) ? data.letterSpacing : 0,
        align: data.textAlign,
        spans: createRichTextSpans(layer),
        writingMode,
        fixedInlineSize,
        width: fixedInlineSize && isHorizontal ? layer.width : undefined,
        height: fixedInlineSize && !isHorizontal ? layer.height : undefined,
    };
};

/**
 * 从图层数据派生列表缩进测量输入。
 *
 * @param layer - ILayerProps，当前文本图层。
 * @returns MeasureListMarkerInput | null，可交给测量原语的列表输入。
 */
export const createListMarkerMeasureInput = (layer: ILayerProps): MeasureListMarkerInput | null => {
    const data = layer.data as ITextLayerProps;
    if (!data.listStyle) return null;

    if (isCharListStyle(data.listStyle)) {
        return {
            type: 'char',
            style: data.listStyle,
            order: countListParagraphs(layer),
            fontFamily: resolveMainFontFamily(layer),
            fontSize: data.fontSize,
        };
    }

    const material = data.listStyleMaterial;
    if (!material) return null;

    return {
        type: 'custom',
        baseContentTextSize: material.baseContentTextSize,
        iconWidth: material.iconWidth,
        scale: material.scale,
        fontSize: data.fontSize,
    };
};

/**
 * 测量当前图层的列表 marker 缩进。
 *
 * 该入口只依赖图层原始文本和样式，可在 snapshot layout 前后复用。固定宽文本在 layout 前用它扣减
 * 正文可用宽度，builder 在 layout 后用同一个值绘制 marker 和计算可见外接盒。
 *
 * @param engine - TextEngine，已经 ready 且注册字体的文本引擎。
 * @param layer - ILayerProps，当前文本图层。
 * @returns number，marker 区域缩进；非列表或测量失败时返回 0。
 */
export const measureListIndent = (engine: TextEngine, layer: ILayerProps): number => {
    const data = layer.data as ITextLayerProps;
    const input = createListMarkerMeasureInput(layer);

    return measureListMarker(engine, input, data.fontSize);
};

/**
 * 测量当前图层的文本内容。
 *
 * @param engine - TextEngine，已经 ready 且注册字体的文本引擎。
 * @param layer - ILayerProps，当前文本图层。
 * @returns 测量结果，包含宽度、高度和最小宽度，失败时返回默认值。
 */
export const measureLayer = (engine: TextEngine, layer: ILayerProps) => {
    const { fontSize, textContents = [], listStyle, letterSpacing } = layer.data as ITextLayerProps;
    const withListMarker = !!listStyle;

    const minInlineSize = (() => {
        let min = fontSize;
        if (textContents.length > 0) {
            textContents.forEach((item) => (min = Math.max(min, item.fontSize)));
        }

        return min + (letterSpacing || 0);
    })();

    const ret = {
        width: layer.width,
        height: layer.height,
        minInlineSize,
    };

    try {
        const input = createMeasureInput(layer);

        const isHorizontal = input.writingMode === 'horizontal-tb';
        const fixedInlineSize = layer.__fixedWidth !== false;
        const layerInlineSize = isHorizontal ? layer.width : layer.height;
        const measureInlineSize = fixedInlineSize ? (layerInlineSize === 0 ? null : layerInlineSize) : null;
        const listIndent = withListMarker ? measureListIndent(engine, layer) : 0;
        const contentInlineSize = measureInlineSize === null ? undefined : Math.max(measureInlineSize - listIndent, 1);

        const result = measureBuffer(engine, {
            ...input,
            fixedInlineSize,
            width: isHorizontal ? contentInlineSize : undefined,
            height: isHorizontal ? undefined : contentInlineSize,
        });

        if (!result) return ret;

        return {
            width: isHorizontal ? (measureInlineSize ?? result.width + listIndent) : result.width,
            height: isHorizontal ? result.height : (measureInlineSize ?? result.height + listIndent),
            minInlineSize: result.minInlineSize,
        };
    } catch (error) {
        console.error('[TextLayer:measureLayer] error', error);
        return ret;
    }
};
